import React, { useEffect, useState } from 'react';
import { Alert, Button, Card, DatePicker, Divider, Form, Input, InputNumber, Modal, Radio, Select, Space, Switch, Table, Tabs, message } from 'antd';
import request from '../../../utils/request';

type ValuationSettings = {
  id?: number;
  factor_cc_per_g: number;
  min_volume_cc_per_unit?: number | null;
  max_volume_cc_per_unit?: number | null;
  is_active: boolean;
  effective_from?: string | null;
  effective_to?: string | null;
  created_at?: string;
  updated_at?: string;
};

async function fetchEffectiveSettings(): Promise<ValuationSettings | null> {
  try {
    const resp = await request.get<ValuationSettings>('/api/v1/inventory/system-valuation-settings/effective/');
    const data: any = resp.data;
    if (!data || !data.id) return null;
    return data;
  } catch (e) {
    return null;
  }
}

async function saveSettings(payload: Omit<ValuationSettings, 'id' | 'created_at' | 'updated_at'> & { id?: number }): Promise<ValuationSettings | null> {
  const url = payload.id ? `/api/v1/inventory/system-valuation-settings/${payload.id}/` : '/api/v1/inventory/system-valuation-settings/';
  if (payload.id) {
    const resp = await request.put<ValuationSettings>(url, payload);
    return resp.data as any;
  } else {
    const resp = await request.post<ValuationSettings>(url, payload);
    return resp.data as any;
  }
}

const AdminSystemBasicSettings: React.FC = () => {
  const [form] = Form.useForm<ValuationSettings>();
  const [loading, setLoading] = useState(false);
  const [current, setCurrent] = useState<ValuationSettings | null>(null);
  const [overlayList, setOverlayList] = useState<any[]>([]);
  const [overlayLoading, setOverlayLoading] = useState(false);
  const [overlaySaving, setOverlaySaving] = useState(false);
  const [overlayModalOpen, setOverlayModalOpen] = useState(false);
  const [overlayEditing, setOverlayEditing] = useState<any>(null);
  const [overlayForm] = Form.useForm();
  const overlayLayoutMode = Form.useWatch('layout_mode', overlayForm);
  const overlayContentSource = Form.useWatch('content_source', overlayForm);
  const overlayOutputMode = Form.useWatch('output_mode', overlayForm);

  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        const s = await fetchEffectiveSettings();
        setCurrent(s);
        if (s) {
          form.setFieldsValue({
            id: s.id,
            factor_cc_per_g: s.factor_cc_per_g,
            min_volume_cc_per_unit: s.min_volume_cc_per_unit ?? undefined,
            max_volume_cc_per_unit: s.max_volume_cc_per_unit ?? undefined,
            is_active: s.is_active,
            effective_from: s.effective_from ? s.effective_from : undefined,
            effective_to: s.effective_to ? s.effective_to : undefined,
          } as any);
        } else {
          form.setFieldsValue({ is_active: true } as any);
        }
      } finally {
        setLoading(false);
      }
    })();
  }, [form]);

  const loadOverlayList = async () => {
    setOverlayLoading(true);
    try {
      const resp = await request.get('/api/v1/logistics/label-overlay-configs/?page_size=200');
      const data: any = resp?.data;
      const list = Array.isArray(data?.results) ? data.results : (Array.isArray(data) ? data : []);
      setOverlayList(list);
    } catch {
      setOverlayList([]);
    } finally {
      setOverlayLoading(false);
    }
  };

  useEffect(() => {
    loadOverlayList();
  }, []);

  const handleSubmit = async () => {
    const values = await form.validateFields();
    const payload: any = {
      id: current?.id,
      factor_cc_per_g: values.factor_cc_per_g,
      min_volume_cc_per_unit: values.min_volume_cc_per_unit ?? null,
      max_volume_cc_per_unit: values.max_volume_cc_per_unit ?? null,
      is_active: values.is_active ?? true,
      effective_from: values.effective_from || null,
      effective_to: values.effective_to || null,
    };
    setLoading(true);
    try {
      const saved = await saveSettings(payload);
      setCurrent(saved);
      message.success('已保存');
    } catch (e: any) {
      message.error(e?.message || '保存失败');
    } finally {
      setLoading(false);
    }
  };

  const openOverlayModal = (row?: any) => {
    setOverlayEditing(row || null);
    const normalizeInputSchema = (schema: any) => {
      if (!schema) return [];
      if (Array.isArray(schema)) return schema;
      if (typeof schema === 'object') {
        return Object.entries(schema).map(([name, cfg]) => ({ name, ...(cfg as any) }));
      }
      return [];
    };
    const normalizeComputeRules = (rules: any) => {
      if (!rules) return [];
      if (Array.isArray(rules)) return rules;
      if (typeof rules === 'object') {
        return Object.entries(rules).map(([name, cfg]) => ({ name, ...(cfg as any) }));
      }
      return [];
    };
    const normalizeOutputTemplate = (tpl: any) => {
      const title = tpl?.title || '';
      if (tpl?.text) {
        return { output_mode: 'text', output_text: tpl.text, output_lines: '', output_title: title };
      }
      if (Array.isArray(tpl?.lines)) {
        return { output_mode: 'lines', output_text: '', output_lines: tpl.lines.join('\n'), output_title: title };
      }
      return { output_mode: 'lines', output_text: '', output_lines: '', output_title: title };
    };
    const layout = row?.layout_config || {};
    const outputTpl = normalizeOutputTemplate(row?.output_template);
    overlayForm.setFieldsValue({
      name: row?.name,
      scenario_code: row?.scenario_code,
      description: row?.description,
      target_type: row?.target_type || 'waybill',
      content_source: row?.content_source || 'dropship_items',
      layout_mode: row?.layout_mode || 'fixed',
      version: row?.version || 1,
      is_active: row?.is_active ?? true,
      input_schema_list: normalizeInputSchema(row?.input_schema),
      compute_rules_list: normalizeComputeRules(row?.compute_rules),
      output_mode: outputTpl.output_mode,
      output_text: outputTpl.output_text,
      output_lines: outputTpl.output_lines,
      output_title: outputTpl.output_title,
      font_size: layout?.font_size ?? 12,
      line_height: layout?.line_height ?? 16,
      show_title: layout?.show_title ?? true,
      fixed_x: layout?.fixed_x ?? 20,
      fixed_y: layout?.fixed_y ?? 20,
      fixed_w: layout?.fixed_w ?? 240,
      fixed_h: layout?.fixed_h ?? 120,
      auto_min_blank_ratio: layout?.auto_min_blank_ratio ?? 0.2,
      auto_margin: layout?.auto_margin ?? 8,
      custom_text: layout?.custom_text ?? '',
    });
    setOverlayModalOpen(true);
  };

  const handleOverlaySubmit = async () => {
    try {
      const values = await overlayForm.validateFields();
      const inputSchema = (values.input_schema_list || []).filter((it: any) => it?.name);
      const buildComputeRule = (row: any) => {
        if (!row?.name || !row?.op) return null;
        const op = row.op;
        if (['sum', 'count_distinct', 'unique'].includes(op)) {
          return { name: row.name, op, path: row.path || '' };
        }
        if (op === 'join') {
          return { name: row.name, op, source: row.source || row.path || row.var || '', sep: row.sep || ' ' };
        }
        if (op === 'concat') {
          const items = String(row.items || '')
            .split(/[,;\n]/)
            .map((s: string) => s.trim())
            .filter(Boolean);
          return { name: row.name, op, items };
        }
        if (op === 'if') {
          return {
            name: row.name,
            op,
            cond: {
              left: row.cond_left,
              op: row.cond_op || '==',
              right: row.cond_right,
            },
            then: row.then,
            else: row.else,
          };
        }
        if (op === 'value') {
          return { name: row.name, op, value: row.value };
        }
        return { name: row.name, op };
      };
      const computeRules = (values.compute_rules_list || [])
        .map(buildComputeRule)
        .filter(Boolean);
      const outputTemplate: any = {};
      if (values.output_title) outputTemplate.title = values.output_title;
      if (values.output_mode === 'text') {
        if (values.output_text) outputTemplate.text = values.output_text;
      } else {
        const lines = String(values.output_lines || '')
          .split('\n')
          .map((s: string) => s.trim())
          .filter(Boolean);
        if (lines.length) outputTemplate.lines = lines;
      }
      const layoutConfig = {
        font_size: Number(values.font_size || 12),
        line_height: Number(values.line_height || 16),
        show_title: !!values.show_title,
        fixed_x: Number(values.fixed_x || 0),
        fixed_y: Number(values.fixed_y || 0),
        fixed_w: Number(values.fixed_w || 0),
        fixed_h: Number(values.fixed_h || 0),
        auto_min_blank_ratio: Number(values.auto_min_blank_ratio || 0),
        auto_margin: Number(values.auto_margin || 0),
        custom_text: values.custom_text || '',
      };
      const payload = {
        name: values.name,
        scenario_code: values.scenario_code,
        description: values.description || '',
        target_type: values.target_type,
        content_source: values.content_source,
        layout_mode: values.layout_mode,
        version: Number(values.version || 1),
        is_active: !!values.is_active,
        input_schema: inputSchema,
        compute_rules: computeRules,
        output_template: outputTemplate,
        layout_config: layoutConfig,
      };
      setOverlaySaving(true);
      if (overlayEditing?.id) {
        await request.put(`/api/v1/logistics/label-overlay-configs/${overlayEditing.id}/`, payload);
      } else {
        await request.post('/api/v1/logistics/label-overlay-configs/', payload);
      }
      message.success('已保存');
      setOverlayModalOpen(false);
      setOverlayEditing(null);
      loadOverlayList();
    } catch (e: any) {
      if (e?.errorFields) return;
      message.error(e?.message || '保存失败');
    } finally {
      setOverlaySaving(false);
    }
  };

  return (
    <div className="p-6">
      <h1 className="text-2xl font-bold mb-2">基础设置</h1>
      <p className="text-gray-600 mb-4">系统配置 / 基础设置</p>
      <Tabs
        defaultActiveKey="base"
        items={[
          {
            key: 'base',
            label: '基础设置',
            children: (
              <>
                <p className="text-gray-600 mb-4">全局异常库存估值配置（重量→体积换算）。</p>
                <Card>
                  <Form form={form} layout="vertical" disabled={loading}>
                    <Form.Item name="factor_cc_per_g" label="重量→体积换算系数 (cc/克)" rules={[{ required: true, message: '请输入换算系数' }]}>
                      <InputNumber min={0} step={0.000001} precision={6} style={{ width: 240 }} />
                    </Form.Item>
                    <Form.Item name="min_volume_cc_per_unit" label="单件最小计费体积 (cc)">
                      <InputNumber min={0} step={0.000001} precision={6} style={{ width: 240 }} />
                    </Form.Item>
                    <Form.Item name="max_volume_cc_per_unit" label="单件最大计费体积 (cc)">
                      <InputNumber min={0} step={0.000001} precision={6} style={{ width: 240 }} />
                    </Form.Item>
                    <Form.Item name="is_active" label="启用" valuePropName="checked" initialValue={true}>
                      <Switch />
                    </Form.Item>
                    <Space size={24} wrap>
                      <Form.Item name="effective_from" label="生效开始(可选)">
                        <InputNumber style={{ display: 'none' }} />
                        <DatePicker allowClear onChange={(d) => form.setFieldValue('effective_from', d ? d.format('YYYY-MM-DD') : null)} />
                      </Form.Item>
                      <Form.Item name="effective_to" label="生效结束(可选)">
                        <InputNumber style={{ display: 'none' }} />
                        <DatePicker allowClear onChange={(d) => form.setFieldValue('effective_to', d ? d.format('YYYY-MM-DD') : null)} />
                      </Form.Item>
                    </Space>
                    <Divider />
                    <Button type="primary" onClick={handleSubmit} loading={loading}>保存</Button>
                  </Form>
                </Card>
              </>
            ),
          },
          {
            key: 'document_mark',
            label: '单据标注',
            children: (
              <Card>
                <Alert
                  type="info"
                  showIcon
                  message="通用标注配置"
                  description="按业务场景维护配置记录，调用方只需传入配置要求的数据即可生成标注文本。"
                  style={{ marginBottom: 16 }}
                />
                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 12 }}>
                  <div className="text-gray-500">共 {overlayList.length} 条配置</div>
                  <Space>
                    <Button onClick={loadOverlayList} loading={overlayLoading}>刷新</Button>
                    <Button type="primary" onClick={() => openOverlayModal()}>新增配置</Button>
                  </Space>
                </div>
                <Table
                  rowKey="id"
                  loading={overlayLoading}
                  dataSource={overlayList}
                  pagination={{ pageSize: 10, showSizeChanger: true, pageSizeOptions: ['10', '20', '50'] }}
                  columns={[
                    { title: '名称', dataIndex: 'name', width: 200 },
                    { title: '场景编码', dataIndex: 'scenario_code', width: 180 },
                    { title: '目标', dataIndex: 'target_type', width: 120 },
                    { title: '内容来源', dataIndex: 'content_source', width: 160 },
                    { title: '标注方式', dataIndex: 'layout_mode', width: 120 },
                    { title: '版本', dataIndex: 'version', width: 80 },
                    { title: '启用', dataIndex: 'is_active', width: 80, render: (v: any) => (v ? '是' : '否') },
                    { title: '更新时间', dataIndex: 'updated_at', width: 180 },
                    {
                      title: '操作',
                      dataIndex: 'op',
                      width: 120,
                      render: (_: any, r: any) => (
                        <Button size="small" type="link" onClick={() => openOverlayModal(r)}>编辑</Button>
                      ),
                    },
                  ]}
                />

                <Modal
                  title={overlayEditing ? '编辑配置' : '新增配置'}
                  open={overlayModalOpen}
                  onCancel={() => setOverlayModalOpen(false)}
                  onOk={handleOverlaySubmit}
                  confirmLoading={overlaySaving}
                  width={760}
                >
                  <Form form={overlayForm} layout="vertical">
                    <Space size={24} wrap>
                      <Form.Item name="name" label="配置名称" rules={[{ required: true, message: '请输入配置名称' }]}>
                        <Input style={{ width: 240 }} />
                      </Form.Item>
                      <Form.Item name="scenario_code" label="场景编码" rules={[{ required: true, message: '请输入场景编码' }]}>
                        <Input style={{ width: 240 }} />
                      </Form.Item>
                      <Form.Item name="version" label="版本号" rules={[{ required: true, message: '请输入版本号' }]}>
                        <InputNumber min={1} step={1} />
                      </Form.Item>
                      <Form.Item name="is_active" label="启用" valuePropName="checked">
                        <Switch />
                      </Form.Item>
                    </Space>
                    <Form.Item name="description" label="说明">
                      <Input.TextArea rows={2} />
                    </Form.Item>
                    <Space size={24} wrap>
                      <Form.Item name="target_type" label="标注目标" rules={[{ required: true, message: '请选择标注目标' }]}>
                        <Select style={{ width: 220 }} options={[
                          { value: 'waybill', label: '运单' },
                          { value: 'box_label', label: '箱标' },
                          { value: 'other', label: '其他' },
                        ]} />
                      </Form.Item>
                      <Form.Item name="content_source" label="内容来源" rules={[{ required: true, message: '请选择内容来源' }]}>
                        <Select style={{ width: 220 }} options={[
                          { value: 'dropship_items', label: '代发指令产品信息' },
                          { value: 'inbound_box_labels', label: '中转原始箱标信息' },
                          { value: 'custom_text', label: '自定义文本' },
                          { value: 'other', label: '其他' },
                        ]} />
                      </Form.Item>
                      <Form.Item name="layout_mode" label="标注方式" rules={[{ required: true, message: '请选择标注方式' }]}>
                        <Radio.Group>
                          <Radio value="fixed">固定位置</Radio>
                          <Radio value="auto">动态空白区域</Radio>
                        </Radio.Group>
                      </Form.Item>
                    </Space>
                    <Divider />
                    <div className="font-medium mb-2">调用字段定义</div>
                    <Form.List name="input_schema_list">
                      {(fields, { add, remove }) => (
                        <>
                          {fields.map((field) => (
                            <Space key={field.key} align="start" wrap style={{ marginBottom: 8 }}>
                              <Form.Item {...field} name={[field.name, 'name']} label="字段名" rules={[{ required: true, message: '必填' }]}>
                                <Input style={{ width: 140 }} />
                              </Form.Item>
                              <Form.Item {...field} name={[field.name, 'label']} label="显示名">
                                <Input style={{ width: 140 }} />
                              </Form.Item>
                              <Form.Item {...field} name={[field.name, 'path']} label="路径">
                                <Input style={{ width: 200 }} placeholder="items.qty" />
                              </Form.Item>
                              <Form.Item {...field} name={[field.name, 'required']} label="必填" valuePropName="checked">
                                <Switch />
                              </Form.Item>
                              <Button onClick={() => remove(field.name)}>删除</Button>
                            </Space>
                          ))}
                          <Button type="dashed" onClick={() => add({ required: false })}>新增字段</Button>
                        </>
                      )}
                    </Form.List>

                    <Divider />
                    <div className="font-medium mb-2">取数规则</div>
                    <Form.List name="compute_rules_list">
                      {(fields, { add, remove }) => (
                        <>
                          {fields.map((field) => (
                            <div key={field.key} style={{ border: '1px solid #f0f0f0', padding: 12, marginBottom: 12 }}>
                              <Space wrap align="start">
                                <Form.Item {...field} name={[field.name, 'name']} label="字段名" rules={[{ required: true, message: '必填' }]}>
                                  <Input style={{ width: 160 }} />
                                </Form.Item>
                                <Form.Item {...field} name={[field.name, 'op']} label="规则" rules={[{ required: true, message: '必选' }]}>
                                  <Select style={{ width: 160 }} options={[
                                    { value: 'sum', label: 'sum-求和' },
                                    { value: 'count_distinct', label: 'count_distinct-去重计数' },
                                    { value: 'unique', label: 'unique-去重' },
                                    { value: 'join', label: 'join-拼接' },
                                    { value: 'concat', label: 'concat-连结' },
                                    { value: 'if', label: 'if-条件' },
                                    { value: 'value', label: 'value-固定值' },
                                  ]} />
                                </Form.Item>
                                <Button onClick={() => remove(field.name)}>删除</Button>
                              </Space>

                              <Form.Item shouldUpdate noStyle>
                                {() => {
                                  const op = overlayForm.getFieldValue(['compute_rules_list', field.name, 'op']);
                                  if (['sum', 'count_distinct', 'unique'].includes(op)) {
                                    return (
                                      <Form.Item {...field} name={[field.name, 'path']} label="路径" rules={[{ required: true, message: '必填' }]}>
                                        <Input placeholder="items.qty" />
                                      </Form.Item>
                                    );
                                  }
                                  if (op === 'join') {
                                    return (
                                      <Space wrap>
                                        <Form.Item {...field} name={[field.name, 'source']} label="来源">
                                          <Input placeholder="codes 或 $codes" style={{ width: 200 }} />
                                        </Form.Item>
                                        <Form.Item {...field} name={[field.name, 'sep']} label="分隔符">
                                          <Input style={{ width: 120 }} />
                                        </Form.Item>
                                      </Space>
                                    );
                                  }
                                  if (op === 'concat') {
                                    return (
                                      <Form.Item {...field} name={[field.name, 'items']} label="片段(逗号/换行分隔)">
                                        <Input.TextArea rows={2} placeholder="M, {total_qty}, {codes}" />
                                      </Form.Item>
                                    );
                                  }
                                  if (op === 'if') {
                                    return (
                                      <Space wrap>
                                        <Form.Item {...field} name={[field.name, 'cond_left']} label="左值">
                                          <Input style={{ width: 160 }} placeholder="sku_count" />
                                        </Form.Item>
                                        <Form.Item {...field} name={[field.name, 'cond_op']} label="比较">
                                          <Select style={{ width: 100 }} options={[
                                            { value: '==', label: '==' },
                                            { value: '!=', label: '!=' },
                                            { value: '>', label: '>' },
                                            { value: '>=', label: '>=' },
                                            { value: '<', label: '<' },
                                            { value: '<=', label: '<=' },
                                          ]} />
                                        </Form.Item>
                                        <Form.Item {...field} name={[field.name, 'cond_right']} label="右值">
                                          <Input style={{ width: 160 }} placeholder="1" />
                                        </Form.Item>
                                        <Form.Item {...field} name={[field.name, 'then']} label="为真">
                                          <Input style={{ width: 120 }} />
                                        </Form.Item>
                                        <Form.Item {...field} name={[field.name, 'else']} label="为假">
                                          <Input style={{ width: 120 }} />
                                        </Form.Item>
                                      </Space>
                                    );
                                  }
                                  if (op === 'value') {
                                    return (
                                      <Form.Item {...field} name={[field.name, 'value']} label="固定值">
                                        <Input style={{ width: 200 }} />
                                      </Form.Item>
                                    );
                                  }
                                  return null;
                                }}
                              </Form.Item>
                            </div>
                          ))}
                          <Button type="dashed" onClick={() => add({ op: 'sum' })}>新增规则</Button>
                        </>
                      )}
                    </Form.List>

                    <Divider />
                    <div className="font-medium mb-2">输出模板</div>
                    <Space size={24} wrap>
                      <Form.Item name="output_title" label="标题(可选)">
                        <Input style={{ width: 220 }} placeholder="分拣信息" />
                      </Form.Item>
                      <Form.Item name="output_mode" label="模板类型" initialValue="lines">
                        <Radio.Group>
                          <Radio value="lines">多行</Radio>
                          <Radio value="text">单行</Radio>
                        </Radio.Group>
                      </Form.Item>
                    </Space>
                    {overlayOutputMode === 'text' ? (
                      <Form.Item name="output_text" label="模板文本">
                        <Input placeholder="{flag}{total_qty} {codes_join}" />
                      </Form.Item>
                    ) : (
                      <Form.Item name="output_lines" label="模板行(每行一条)">
                        <Input.TextArea rows={3} placeholder="{flag}{total_qty} {codes_join}" />
                      </Form.Item>
                    )}

                    <Divider />
                    <div className="font-medium mb-2">布局参数</div>
                    <Space size={24} wrap>
                      <Form.Item name="font_size" label="字号">
                        <InputNumber min={8} max={28} step={1} />
                      </Form.Item>
                      <Form.Item name="line_height" label="行高">
                        <InputNumber min={10} max={36} step={1} />
                      </Form.Item>
                      <Form.Item name="show_title" label="显示标题" valuePropName="checked">
                        <Switch />
                      </Form.Item>
                    </Space>
                    {overlayContentSource === 'custom_text' ? (
                      <Form.Item name="custom_text" label="自定义文本">
                        <Input.TextArea rows={3} placeholder="每行一条" />
                      </Form.Item>
                    ) : null}
                    {overlayLayoutMode === 'fixed' ? (
                      <Space size={24} wrap>
                        <Form.Item name="fixed_x" label="起点X">
                          <InputNumber min={0} step={1} />
                        </Form.Item>
                        <Form.Item name="fixed_y" label="起点Y">
                          <InputNumber min={0} step={1} />
                        </Form.Item>
                        <Form.Item name="fixed_w" label="宽度">
                          <InputNumber min={40} step={1} />
                        </Form.Item>
                        <Form.Item name="fixed_h" label="高度">
                          <InputNumber min={40} step={1} />
                        </Form.Item>
                      </Space>
                    ) : null}
                    {overlayLayoutMode === 'auto' ? (
                      <Space size={24} wrap>
                        <Form.Item name="auto_min_blank_ratio" label="最小空白比例">
                          <InputNumber min={0.05} max={0.9} step={0.05} />
                        </Form.Item>
                        <Form.Item name="auto_margin" label="边距">
                          <InputNumber min={0} max={60} step={1} />
                        </Form.Item>
                      </Space>
                    ) : null}
                  </Form>
                </Modal>
              </Card>
            ),
          },
        ]}
      />
    </div>
  );
};

export default AdminSystemBasicSettings;


